import { gql } from '@apollo/client';
import { crewMemberFragments } from '../crew-member';

const fragments = {
  fillerMaterial: gql`
    fragment FillerMaterial on RodFillerMaterial {
      id
      label
      rodFillerMaterialDetails {
        id
        diameter {
          id
          label
        }
        rodHeatLotNumber {
          id
          label
        }
      }
    }
  `,
  rodRequestItem: gql`
    fragment RodRequestItem on RodRequest {
      id
      currentStatusI18n
      pickUpTime
      rodRoom {
        id
      }
      weldProcedure {
        id
        procedure
      }
      comments {
        id
        comment
        createdAt
      }
      createdAt
    }
  `,
  rodRequestWeldItem: gql`
    fragment RodRequestWeldItem on Weld {
      id
      tag
      pipeDiameter
      pipeMaterial
      type {
        id
        name
      }
      commodityAssignees {
        assignee {
          ...CrewMemberStencil
        }
        personaType
      }
    }
    ${crewMemberFragments.crewMemberStencil}
  `,
  rodRequestFillerMaterialItem: gql`
    fragment RodRequestFillerMaterialItem on RodRequestFillerMaterial {
      id
      diameters {
        id
        label
      }
      fillerMaterial {
        id
        label
        rodFillerMaterialDetails {
          id
          diameter {
            id
            label
          }
          rodHeatLotNumber {
            id
            label
          }
        }
        type
      }
    }
    ${crewMemberFragments.crewMemberStencil}
  `,
};

const rodRequestDetailsFragment = gql`
  fragment RodRequestDetails on RodRequest {
    ...RodRequestItem
    requestNumber
    caddies {
      id
      caddy {
        id
        label
        status
      }
      currentStatusI18n
      fillerMaterial {
        id
        diameters {
          diameter {
            id
            label
          }
          heatLotNumber {
            id
            label
          }
        }
        fillerMaterial {
          ...FillerMaterial
        }
      }
      issuedAt
      issuedBy {
        ...CrewMemberStencil
      }
      pickedUpBy {
        ...CrewMemberStencil
      }
      rodRequest {
        id
      }
      stencils {
        ...CrewMemberStencil
      }
    }
    welds {
      ...RodRequestWeldItem
    }
    createdBy {
      ...CrewMemberStencil
      crew {
        crewNumber
      }
    }
    fillerMaterials {
      ...RodRequestFillerMaterialItem
    }
    stencils {
      status
      welds {
        id
        status
        weld {
          id
        }
      }
      stencil {
        ...CrewMemberStencil
        supervisor {
          id
          firstName
          lastName
        }
      }
    }
  }
  ${crewMemberFragments.crewMemberStencil}
  ${fragments.rodRequestItem}
  ${fragments.rodRequestWeldItem}
  ${fragments.rodRequestFillerMaterialItem}
  ${fragments.fillerMaterial}
`;

export const GET_ROD_ROOMS = gql`
  query rodRooms {
    rodRooms {
      id
      name
      isActive
      locationDescription
      location {
        latitude
        longitude
      }
    }
  }
`;

export const GET_ROD_REQUESTS_DETAILS_BY_IDS = gql`
  query rodRequestsByIds($ids: [ID!]!) {
    rodRequestsByIds(ids: $ids) {
      ...RodRequestDetails
    }
    ${rodRequestDetailsFragment}
  }
`;

export const GET_ROD_FILLER_MATERIALS = gql`
  query rodFillerMaterial($paging: PagingInput) {
    rodFillerMaterials(paging: $paging) {
      items {
        ...FillerMaterial
      }
    }
    ${fragments.fillerMaterial}
  }
`;

export const CREATE_ROD_REQUEST = gql`
  mutation createRodRequest($input: CreateRodRequestInput!) {
    createRodRequest(input: $input) {
      error
      rodRequest {
        id
        requestNumber
      }
    }
  }
`;

export const UPDATE_ROD_REQUEST = gql`
  mutation updateRodRequest($input: UpdateRodRequestInput!) {
    updateRodRequest(input: $input) {
      error
      rodRequest {
        id
        requestNumber
        pickUpTime
        weldProcedure {
          id
        }
        fillerMaterials {
          ...RodRequestFillerMaterialItem
        }
        stencils {
          stencil {
            id
          }
        }
        welds {
          ...RodRequestWeldItem
        }
        comments {
          id
          comment
        }
        rodRoom {
          id
        }
        stencils {
          stencil {
            ...CrewMemberStencil
            supervisor {
              id
              firstName
              lastName
            }
          }
        }
      }
    }
    ${fragments.rodRequestWeldItem}
    ${crewMemberFragments.crewMemberStencil}
    ${fragments.rodRequestFillerMaterialItem}
  }
`;

export const ROD_REQUESTS = gql`
  query rodRequests($input: RodRequestFilterInput!) {
    rodRequests(input: $input) {
      items {
       ...RodRequestItem
        fillerMaterials {
          id
          fillerMaterial {
            ...FillerMaterial
          }
          diameters {
            id
            label
          }
        }
        stencils {
          stencil {
            id
            firstName
            lastName
            imageUrl
          }
        }
      }
    }
    ${fragments.fillerMaterial}
    ${fragments.rodRequestItem}
  }
`;

export const WELDER_ROD_REQUESTS = gql`
  query rodRequests($input: RodRequestFilterInput!, $paging: PagingInput!) {
    rodRequests(input: $input, paging: $paging) {
      cursor
      total
      items {
       createdBy{
        id
        firstName
        lastName
       }
       rodRoom{
       id
       name
       }
        ...RodRequestItem
        requestNumber
        fillerMaterials {
          id
          fillerMaterial {
            ...FillerMaterial
          }
          diameters {
            id
            label
          }
        }
        stencils {
          stencil {
            ...CrewMemberStencil
            supervisor {
              id
            }
          }
        }
        welds {
          id
          tag
          pipeMaterial
          pipeDiameter
          type {
            id
            name
          }
          applicableWeldProcedures {
            id
            procedure
          }
        }
      }
    }
    ${fragments.fillerMaterial}
    ${fragments.rodRequestItem}
    ${crewMemberFragments.crewMemberStencil}
  }
`;

export const GET_ROD_REQUESTS = gql`
  query rodRequests($input: RodRequestFilterInput!, $paging: PagingInput!) {
    rodRequests(input: $input, paging: $paging) {
      cursor
      total
      items {
        id
        requestNumber
        currentStatusI18n
        welds {
          id
        }
        stencils {
          stencil {
            id
          }
        }
        createdAt
      }
    }
  }
`;

export const CREATE_ROD_REQUEST_CADDY = gql`
  mutation createRodRequestCaddy($input: CreateRodRequestCaddyInput!) {
    createRodRequestCaddy(input: $input) {
      errorCaddies {
        errorCode
        caddy {
          id
          label
        }
      }
      rodRequest {
        id
        requestNumber
        caddies {
          caddy {
            id
            status
          }
        }
      }
    }
  }
`;

export const DELELTE_ROD_REQUEST = gql`
  mutation deleteRodRequest($input: DeleteRodRequestInput!) {
    deleteRodRequest(input: $input) {
      rodRequest {
        id
        currentStatusI18n
      }
      error
    }
  }
`;

export const DELETE_CADDY = gql`
  mutation deleteCaddy($input: DeleteCaddyInput!) {
    deleteCaddy(input: $input) {
      caddy {
        id
        currentStatusI18n
      }
      error
    }
  }
`;

export const GET_CADDIES = gql`
  query caddies($input: CaddyFilterInput!, $paging: PagingInput) {
    caddies(input: $input, paging: $paging) {
      items {
        id
        label
        status
      }
    }
  }
`;

export const ISSUE_CADDIES = gql`
  mutation issueCaddies ($input: IssueCaddiesInput!) {
    issueCaddies(input: $input) {
      caddies {
        id
        caddy {
          id
          label
          status
        }
        fillerMaterial {
          id
          fillerMaterial {
            id
            label
            rodFillerMaterialDetails {
              id
              diameter {
                id
                label
              }
              rodHeatLotNumber {
                id
                label
              }
            }
          }
        }
        currentStatusI18n
        createdBy {
          ...CrewMemberStencil
        }
        issuedBy {
          ...CrewMemberStencil
        }
        pickedUpBy {
          ...CrewMemberStencil
        }
        stencils {
          ...CrewMemberStencil
        }
        issuedAt
        rodRequest {
          ...RodRequestDetails
        }
      }
      error
    }
    ${crewMemberFragments.crewMemberStencil}
    ${rodRequestDetailsFragment}
  }
`;

export const RETURN_CADDIES = gql`
  mutation returnCaddies($input: ReturnCaddiesInput!) {
    returnCaddies(input: $input) {
      caddies {
        id
        currentStatusI18n
        caddy {
          id
          status
        }
        rodRequest {
          id
          currentStatusI18n
        }
      }
      erroredCaddies {
        caddy {
          caddy {
            label
          }
        }
        errorCode
      }
    }
  }
`;

export const CLOSE_ROD_REQUEST = gql`
  mutation closeRodRequest($input: CloseRodRequestInput!) {
    closeRodRequest(input: $input) {
      error
      rodRequest {
        id
        currentStatusI18n
      }
    }
  }
`;

export const UPDATE_CADDY = gql`
  mutation updateCaddy($input: UpdateCaddyInput!) {
    updateCaddy(input: $input) {
      caddy {
        id
        rodRequest {
          ...RodRequestDetails
        }
      }
      error
    }
    ${rodRequestDetailsFragment}
  }
`;

export const ROD_REQUEST_CADDIES = gql`
  query rodRequestCaddies($input: RodRequestCaddyFilterInput!, $paging: PagingInput) {
    rodRequestCaddies(input: $input, paging: $paging) {
      total
      items {
        id
        currentStatusI18n
        issuedAt
        caddy {
          id
          label
          status
        }
        rodRequest {
          id
          requestNumber
          rodRoom {
            id
          }
          stencils {
           stencil {
            ...CrewMemberStencil
           }
          }
        }
      }
      cursor
      errors
    }
    ${crewMemberFragments.crewMemberStencil}
  }
`;

export const SUBSCRIPTION_ROD_REQUEST_UPDATED = gql`
  subscription onRodRequestUpdated {
    rodRequestsUpdated {
      rodRequestIds
    }
  }
`;

export const SUBSCRIPTION_ROD_REQUEST_CREATED = gql`
  subscription onRodRequestsCreated {
    rodRequestsCreated {
      rodRequestIds
    }
  }
`;

export const ROD_ROOM_STATS_SUMMARY = gql`
  query rodRoomStatsSummary($input: RodRoomStatsInput!) {
    rodRoomStatsSummary(input: $input) {
      id
      errors
      caddyStats {
        caddySummary {
          stats
          status
        }
        numCaddies
      }
      rodRequestStats {
        rodRequestSummary {
          status
          stats
        }
        numRodRequests
      }
    }
  }
`;

export const UPDATE_ROD_ROOM = gql`
  mutation updateRodRoom($input: UpdateRodRoomInput!) {
    updateRodRoom(input: $input) {
      error
      rodRoom {
        id
        isActive
        location {
          latitude
          longitude
        }
      }
    }
  }
`;
export const ROD_REQUEST_STATS_BY_WELDER_AND_ROD_ROOM_NAME = gql`
  query rodRequestStatsByCrewMember($input: RodRequestStatsByCrewMemberInput, $paging: PagingInput) {
    rodRequestStatsByCrewMember(input: $input, paging: $paging) {
      total
      cursor
      items {
        crewMember {
          id
          firstName
          lastName
          imageUrl
          badgeNumber
        }
        rodRequestStatusStats {
          status
          stats
        }
      }
    }
  }
`;
export const GET_ROD_REQUESTS_BY_WELDER_ID = gql`
  query rodRequests($input: RodRequestFilterInput!, $paging: PagingInput!) {
    rodRequests(input: $input, paging: $paging) {
      items {
        id
        requestNumber
        currentStatusI18n
        stencils {
          stencil {
            id
          }
        }
        caddies {
          currentStatusI18n
        }
        rodRoom {
          id
        }
        stencils {
          stencil {
            id
          }
          status
        }
      }
    }
  }
`;

export const CLOSE_ROD_REQUEST_BY_STENCIL_ID = gql`
  mutation closeRodRequestByStencilId($input: CloseRodRequestByStencilIdInput!) {
    closeRodRequestByStencilId(input: $input) {
      erroredRodRequests {
        errorCode
        rodRequest {
          id
          requestNumber
          currentStatusI18n
        }
      }
      rodRequests {
        id
        currentStatusI18n
        stencils {
          stencil {
            id
          }
          status
        }
      }
    }
  }
`;

export const GET_ROD_REQUESTS_STENCILS = gql`
  query rodRequests($input: RodRequestFilterInput!) {
    rodRequests(input: $input) {
      items {
        id
        stencils {
          stencil {
            id
            firstName
            lastName
            badgeNumber
            imageUrl
          }
        }
      }
    }
  }
`;

export const PROGRESS_ROD_REQUESTS = gql`
  query rodRequests($input: RodRequestFilterInput!, $paging: PagingInput!) {
    rodRequests(input: $input, paging: $paging) {
      cursor
      total
      items {
        id
        requestNumber
        welds {
          tag
          id
          pipeDiameter
          pipeMaterial
          type {
            id
            name
          }
        }
        stencils {
          status
          welds {
            id
            status
            weld {
              id
            }
          }
        }
      }
    }
  }
`;
export const MAP_MARKER_ROD_ROOM_STATS_SUMMARY = gql`
  query mapMarkerRodRoomStatsSummary($input: RodRoomStatsInput!) {
    rodRoomStatsSummary(input: $input) {
      errors
      rodRoomStatsSummary {
        status
        stats
      }
      numPriority
    }
  }
`;
